/**
 * Sign in / sign up for standalone customers.
 *
 * One component, two modes, because the fields overlap almost entirely and two
 * near-identical files drift apart within a month.
 *
 * The signup form asks for a website URL rather than treating it as optional.
 * Without it we cannot generate a pixel, and without a pixel a standalone
 * account has no revenue data at all — the optimiser would train on nothing and
 * cheerfully scale a campaign that has sold zero units. Better to make it a
 * required field at the front door than to discover it empty on day fourteen.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, auth, setStoreId, ApiError, type Account } from '../api';

type Mode = 'signin' | 'signup';

export default function SignIn({
  onAuthed,
  initialMode = 'signin',
}: {
  onAuthed?: (a: Account) => void;
  initialMode?: Mode;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [website, setWebsite] = useState('');
  const [storeName, setStoreName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setField(null);
    try {
      const res =
        mode === 'signup'
          ? await api.signup({
              email,
              password,
              websiteUrl: website,
              storeName: storeName || undefined,
            })
          : await api.login(email, password);

      auth.set(res.token);
      setStoreId(res.account.storeId);
      if (onAuthed) onAuthed(res.account);
      else navigate('/app');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // Put the error on the field it belongs to rather than making the
        // customer guess which of four inputs is wrong.
        if (err.code === 'email_taken' || err.code === 'invalid_email') setField('email');
        else if (err.code === 'weak_password') setField('password');
        else if (err.code === 'invalid_website') setField('website');
        else if (err.status === 429) setError('Too many attempts. Wait a minute and try again.');
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const isSignup = mode === 'signup';

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <Link className="auth-brand" to="/">
          ScaleAds AI<small>OS 3.0</small>
        </Link>

        <h1>{isSignup ? 'Create your account' : 'Sign in'}</h1>
        <p className="auth-sub">
          {isSignup
            ? '14-day trial. No card required. Connect one ad account and see recommendations in about ten minutes.'
            : 'Welcome back.'}
        </p>

        <form onSubmit={submit} noValidate>
          <label className="field">
            <span>Email</span>
            <input
              className={`input ${field === 'email' ? 'bad' : ''}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
            />
          </label>

          {isSignup && (
            <>
              <label className="field">
                <span>Your website</span>
                <input
                  className={`input ${field === 'website' ? 'bad' : ''}`}
                  placeholder="example.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  required
                />
                <em className="hint">
                  This is where your pixel goes. It's how we measure revenue, so it has to be the
                  site people actually buy on.
                </em>
              </label>

              <label className="field">
                <span>
                  Business name <em className="opt">optional</em>
                </span>
                <input
                  className="input"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                />
              </label>
            </>
          )}

          <label className="field">
            <span>Password</span>
            <input
              className={`input ${field === 'password' ? 'bad' : ''}`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              required
            />
            {isSignup && (
              <em className="hint">
                At least 12 characters. Length beats symbols — a passphrase you can remember is
                stronger than <code>Passw0rd!</code>
              </em>
            )}
          </label>

          {!isSignup && (
            <p className="auth-switch"><Link to="/forgot-password">Forgot your password?</Link></p>
          )}

          {error && <p className="auth-error">{error}</p>}

          <button className="btn brand auth-submit" type="submit" disabled={busy}>
            {busy ? 'Working…' : isSignup ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="auth-switch">
          {isSignup ? 'Already have an account?' : 'New to ScaleAds AI?'}{' '}
          <button
            type="button"
            onClick={() => {
              setMode(isSignup ? 'signin' : 'signup');
              setError(null);
              setField(null);
            }}
          >
            {isSignup ? 'Sign in' : 'Create one'}
          </button>
        </p>

        <p className="auth-alt">
          Run your store on Shopify? <Link to="/shopify">See the Shopify edition</Link> for
          order-level revenue data and an embedded workflow.
        </p>
      </div>
    </div>
  );
}
